"use client"

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import { SearchIcon } from "@/components/ui/icons"
import { useLocalePath } from "@/lib/use-localized-navigation"
import {
  groupHitsByType,
  runSearch,
  type ResultGroup,
} from "@/lib/search/client"
import type { SearchHit } from "@/lib/search/engine"
import { track } from "@/lib/analytics"
import { AiAnswerLane } from "@/components/search/ai-answer-lane"
import { aiAssistantEnabled } from "@/lib/release-flags"
import { isAllowedInCountry } from "@/lib/country-gates"
import { SearchResultsView } from "@/components/search/search-results-view"
import { SEARCH_FOCUS_EVENT } from "@/components/search/search-provider"
import { useKbFallback } from "@/components/search/use-kb-fallback"

const SEARCH_DEBOUNCE_MS = 200
const QUERY_TRACK_DEBOUNCE_MS = 1500

export default function SearchPageClient() {
  const t = useTranslations("search")
  const locale = useLocale()
  const { code: country } = useCountry()
  const localePath = useLocalePath()
  const searchParams = useSearchParams()
  const router = useRouter()

  const initialQuery = searchParams.get("q") ?? ""
  const [rawQuery, setRawQuery] = useState(initialQuery)
  const [debouncedQuery, setDebouncedQuery] = useState(initialQuery)
  const [groups, setGroups] = useState<ResultGroup[] | null>(null)
  const [flatHits, setFlatHits] = useState<SearchHit[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastTrackedQueryRef = useRef<{ query: string; count: number } | null>(
    null
  )
  const trackOpenedOnceRef = useRef(false)

  // Track that the page surface was visited — once per mount.
  useEffect(() => {
    if (trackOpenedOnceRef.current) return
    trackOpenedOnceRef.current = true
    track.searchOpened({ source: "page", country, locale })
  }, [country, locale])

  // The navbar magnifying glass + Cmd-K dispatch this event when the user
  // is already on the /search page, so we focus the input here instead of
  // stacking the modal on top of the results.
  useEffect(() => {
    function onFocusEvent() {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
    window.addEventListener(SEARCH_FOCUS_EVENT, onFocusEvent)
    return () => window.removeEventListener(SEARCH_FOCUS_EVENT, onFocusEvent)
  }, [])

  // Sync URL → state when the user navigates with browser back/forward.
  // We deliberately don't push to the URL on every keystroke (only on
  // submit) — that would spam history and slow typing.
  useEffect(() => {
    const next = searchParams.get("q") ?? ""
    setRawQuery(next)
    setDebouncedQuery(next)
  }, [searchParams])

  // Debounce typed input → debouncedQuery.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedQuery(rawQuery)
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [rawQuery])

  // Run the search whenever the debounced query changes.
  useEffect(() => {
    let cancelled = false
    const trimmed = debouncedQuery.trim()
    if (!trimmed) {
      setGroups(null)
      setFlatHits([])
      setLoading(false)
      setSelectedIndex(0)
      return
    }

    setLoading(true)
    runSearch(country, locale, trimmed, { limit: 60 })
      .then((hits) => {
        if (cancelled) return
        setFlatHits(hits)
        setGroups(groupHitsByType(hits))
        setLoading(false)
        setSelectedIndex(0)
      })
      .catch(() => {
        if (cancelled) return
        setGroups([])
        setFlatHits([])
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery, country, locale])

  const kbFallback = useKbFallback({
    active: true,
    query: debouncedQuery,
    zeroResults: !loading && !!debouncedQuery.trim() && flatHits.length === 0,
    country,
    locale,
    surface: "page",
  })

  // PostHog `search_query` — mirrors the modal: debounce + dedupe.
  useEffect(() => {
    const trimmed = debouncedQuery.trim()
    if (trimmed.length < 3 || loading) return
    const handle = window.setTimeout(() => {
      const count = flatHits.length
      const last = lastTrackedQueryRef.current
      if (last && last.query === trimmed && last.count === count) return
      lastTrackedQueryRef.current = { query: trimmed, count }
      track.searchQuery({
        query: trimmed,
        result_count: count,
        surface: "page",
        source: "page",
        locale,
        zero_results: count === 0,
      })
    }, QUERY_TRACK_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [debouncedQuery, flatHits, loading, locale])

  const onResultClick = useCallback(
    (hit: SearchHit, rank: number) => {
      track.searchResultClicked({
        query: debouncedQuery.trim(),
        url: hit.doc.url,
        rank,
        type: hit.doc.type,
        surface: "page",
        country,
        locale,
      })
    },
    [debouncedQuery, country, locale]
  )

  const onPromptClick = useCallback(
    (query: string, promptId: string) => {
      track.searchPromptClicked({
        prompt_id: promptId,
        surface: "page",
        country,
        locale,
      })
      router.push(`${localePath("/search")}?q=${encodeURIComponent(query)}`)
    },
    [country, locale, router, localePath]
  )

  const handleInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setRawQuery(event.target.value)
  }, [])

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      const trimmed = rawQuery.trim()
      const target = trimmed
        ? `${localePath("/search")}?q=${encodeURIComponent(trimmed)}`
        : localePath("/search")
      // `replace` so back-button doesn't accumulate every search variant.
      router.replace(target)
    },
    [rawQuery, router, localePath]
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault()
        if (flatHits.length === 0) return
        setSelectedIndex((idx) => (idx + 1) % flatHits.length)
        return
      }
      if (event.key === "ArrowUp") {
        event.preventDefault()
        if (flatHits.length === 0) return
        setSelectedIndex((idx) => (idx - 1 + flatHits.length) % flatHits.length)
        return
      }
      if (
        event.key === "Enter" &&
        flatHits.length > 0 &&
        selectedIndex < flatHits.length
      ) {
        event.preventDefault()
        const hit = flatHits[selectedIndex]
        onResultClick(hit, selectedIndex)
        router.push(hit.doc.url)
      }
    },
    [flatHits, selectedIndex, onResultClick, router]
  )

  const heading = useMemo(() => {
    const trimmed = debouncedQuery.trim()
    if (!trimmed) return t("pageHeading")
    return t("pageHeadingForQuery", { query: trimmed })
  }, [debouncedQuery, t])

  return (
    <div className="bg-background min-h-screen">
      <div className="mx-auto max-w-[760px] px-4 py-12 md:px-6 md:py-16">
        <h1 className="type-title-xl mb-6 text-neutral-900">{heading}</h1>
        <form onSubmit={onSubmit} className="mb-8">
          <label
            htmlFor="search-page-input"
            className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-neutral-900/10"
          >
            <SearchIcon
              width={22}
              height={22}
              className="shrink-0 text-neutral-500"
            />
            <input
              id="search-page-input"
              ref={inputRef}
              type="search"
              inputMode="search"
              autoComplete="off"
              spellCheck={false}
              value={rawQuery}
              onChange={handleInput}
              onKeyDown={onKeyDown}
              placeholder={t("placeholder")}
              aria-label={t("placeholder")}
              className="type-body-lg w-full bg-transparent text-neutral-900 outline-none placeholder:text-neutral-400"
            />
          </label>
        </form>
        {isAllowedInCountry("feature:chat", country) && (
          <AiAnswerLane
            surface="search-page"
            scope="general"
            country={country}
            locale={locale}
            pageUrl={`/${country}/${locale}/search`}
            pageTitle="Search"
            envEnabled={aiAssistantEnabled()}
            query={debouncedQuery}
          />
        )}
        <div className="overflow-hidden rounded-2xl bg-white">
          <SearchResultsView
            query={debouncedQuery}
            groups={groups}
            loading={loading}
            flatHits={flatHits}
            selectedIndex={selectedIndex}
            onHover={setSelectedIndex}
            onResultClick={onResultClick}
            onPromptClick={onPromptClick}
            surface="page"
            country={country}
            locale={locale}
            kbFallback={kbFallback}
          />
        </div>
      </div>
    </div>
  )
}
